import { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { useLocation } from "react-router";
import { useStore } from "@/stores/store-context";
import { USERPILOT_APP_ID } from "@/config";
import {
  initUserpilot,
  identifyUserpilot,
  identifyUserpilotById,
  anonymizeUserpilot,
  reloadUserpilot,
  userpilotIsInitialized,
} from "@/userpilot-sdk";
import {
  completePendingSignOut,
  discardPendingSignOut,
  flushPendingAuthEvent,
} from "@/lib/auth-analytics";

export const UserpilotUserSync = observer(function UserpilotUserSync() {
  const { user, workspace, featureFlags } = useStore();
  const location = useLocation();

  const flagOn = featureFlags.isReady ? featureFlags.isEnabled("userpilot-enabled", false) : false;
  const enabled = USERPILOT_APP_ID.length > 0 && flagOn;

  const currentUser = user.currentUser;
  const currentWorkspace = workspace.currentWorkspace;
  // Snapshot the ids in the observer render scope (a reactive read) so the
  // effects below — which run untracked — key off primitives instead of
  // dereferencing the observables, which would trip observableRequiresReaction.
  const userId = currentUser?.id ?? null;
  // The workspace resolves after the user on a real load, so identify has to
  // re-run when it arrives. Keyed on the user alone, it bailed on the missing
  // workspace and never ran again, and no user ever reached Userpilot (NG-2029).
  const workspaceId = currentWorkspace?.id ?? null;

  useEffect(() => {
    if (!enabled) return;
    initUserpilot(USERPILOT_APP_ID);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (!userpilotIsInitialized()) return;
    if (!currentUser || !currentWorkspace) return;
    // identifyUserpilot reads many observable fields off the user/workspace to
    // build its payload. The effect runs outside React's render reaction, so do
    // those one-shot reads inside an action to avoid the reactivity warning.
    runInAction(() => identifyUserpilot(currentUser, currentWorkspace));
    // A sign-in that just landed is sent now, attached to the user it identified.
    flushPendingAuthEvent();
    // Still signed in, so a sign-out recorded before this load never completed.
    discardPendingSignOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId, workspaceId]);

  // The session is gone: a sign-out recorded before this load completed.
  const sessionKnownAbsent = user.sessionKnownAbsent;
  useEffect(() => {
    if (!enabled || !sessionKnownAbsent) return;
    if (!userpilotIsInitialized()) return;
    // Sent as the person who signed out, then back to the anonymous visitor this
    // signed-out page is.
    completePendingSignOut((signedOutUserId, send) => {
      identifyUserpilotById(signedOutUserId);
      send();
      anonymizeUserpilot();
    });
  }, [enabled, sessionKnownAbsent]);

  // With Userpilot resolved off there is nothing to wait for: settle the pending
  // events now, so they reach whatever `track` sends to rather than waiting on an
  // identify that will never run. With it on, they wait for identify above, so
  // they attach to the user.
  const userpilotResolvedOff = featureFlags.isReady && !enabled;
  useEffect(() => {
    if (!userpilotResolvedOff) return;
    if (userId) {
      flushPendingAuthEvent();
      discardPendingSignOut();
    } else if (sessionKnownAbsent) {
      completePendingSignOut();
    }
  }, [userpilotResolvedOff, userId, sessionKnownAbsent]);

  const previousUserIdRef = useRef<string | null>(userId);
  useEffect(() => {
    const previous = previousUserIdRef.current;
    previousUserIdRef.current = userId;
    if (!userpilotIsInitialized()) return;
    if (previous && !userId) {
      anonymizeUserpilot();
    }
  }, [userId]);

  const isFirstRouteRef = useRef(true);
  useEffect(() => {
    if (isFirstRouteRef.current) {
      isFirstRouteRef.current = false;
      return;
    }
    if (!userpilotIsInitialized()) return;
    reloadUserpilot();
  }, [location.pathname, location.search]);

  return null;
});
